(ns dashboard.entity.document.evidence
  "The principle contract of the dashboard.entity component namespaces are this:
     1. All components will NOT depend on keyword references to subs and events in
        external NSes, but rather receive data as arguments, functions and callbacks.
     2. All internal reliance on the re-frame app DB must be internally contained.
     3. All re-frame app subs and events must NOT be directly referenced by outside
        NSes, but rather via GETTER and SETTER functions - the same is true of internal
        calls - prefer using parameterized functions.
     4. The entity is a UI component that may encapsulate low-level business rules on
        UI controls, but is not responsible for hydrating components with data or entrenched
        references to component namespaces. The UI is customizable, and top-level entities
        should have configurable and interchangable UI elements."
  (:require ["react" :as react]
            [clojure.string :as str]
            [dashboard.entity.analysis.modals :as analysis.modals]
            [dashboard.entity.analysis.search :as analysis.search]
            [dashboard.entity.analysis.tree :as analysis.tree]
            [dashboard.entity.document.analysis-search :as document.analysis-search]
            [dashboard.entity.evidence.fields :as evidence.fields]))

(defn abridged-content-blockquote
  [text]
  [:div.content.my-0.mx-3
   [:blockquote
    (if (> (count text) 100)
      [:<>
       (apply str (take 50 text))
       [:b " ... "]
       (apply str (drop (- (count text) 50) text))]
      (str text))]])

(defn viewer-analysis-search
  "Displays analysis search and the evidence creation pane."
  [{:keys [attribution-props
           on-save-evidence
           search-query
           saving
           selected-constructs
           on-cancel-evidence
           set-selected-constructs]}]
  (let [analyses @(analysis.search/all-analysis-structures)
        text (:text search-query)]
    [:div.is-flex-grow-1.document-viewer-analysis-search
     [:div.level
      [:div.level-left.mb-2
      (when selected-constructs
       [:a.is-info.mr-4
       {:key (keyword :construct (:construct-id selected-constructs))
        :on-click (fn [_] (set-selected-constructs nil))}
       [:i.icon.fas.fa-chevron-left]])
      [:h5.title.is-5.my-0
       (if-not selected-constructs
         "Select an Analysis to Provide Evidence for (step 1 of 2)"
         "Select an Analysis to Provide Evidence for (step 2 of 2)")]]
      [:button.button
       {:on-click (fn [_]
                    (on-cancel-evidence nil)
                    (set-selected-constructs nil))}
       [:i.icon.fas.fa-close ]]]
     [:div.level.prototype-content
      [:div.prototype-search-sidebar
       (if selected-constructs
         [:<>
          (into [:div.mb-3]
                (mapv
                 (fn [selected-construct]
                   [:h1.title.is-5.my-0 (:construct-id selected-construct)
                    " - "
                    (:construct selected-construct)])
                 selected-constructs))
          [:div.is-size-7.content
           [:blockquote (if (> (count text) 500)
                          [:<>
                           (apply str (take 250 text))
                           [:b " ... "]
                           (apply str (drop (- (count text) 250) text))]
                          text)]]
          [evidence.fields/Reference attribution-props [] identity 0 identity {} nil identity]
          [:div [:f> evidence.fields/Comment attribution-props]]
          [evidence.fields/ControlStatus attribution-props]
          [:div.buttons
           [:button.button.is-primary
            {:on-click on-save-evidence
             :disabled saving}
            "Save as Evidence"]
           [:button.button.is-danger.is-outlined
            {:on-click on-cancel-evidence
             :disabled saving}
            "Cancel"]]]
       ;; Else
       ;; Analysis Selection
         [:<>
          [:div.is-size-7.content
           [:blockquote (if (> (count text) 500)
                          [:<>
                           (apply str (take 250 text))
                           [:b " ... "]
                           (apply str (drop (- (count text) 250) text))]
                          text)]]
          [evidence.fields/Reference attribution-props [] identity 0 identity {} nil identity]

          [:div.document-viewer-left-sidebar
           [:f> document.analysis-search/analysis-by-category-tree
            {:analyses analyses
             :applied-analysis-version-ids #{}
             :on-submit (comp set-selected-constructs)
             :disabled-construct-ids (-> search-query :evidence :construct-ids set)
             :question {:content text}
             :category nil
             :on-click-construct identity
             :on-click-state identity}]]])]]]))

(defn extract-top-percentage
  "Given an html string that is known to specify a span tag with a top property,
   we extract that property into a float"
  [html-string]
  (when-let [match (re-find #"top: ([^;]+);" (or html-string "none"))]
    (let [value (or (second match) "")]
      (if (re-find #"calc\((.+)\)" value)
        (let [calc-expression (second (re-find #"calc\((.+)\)" value))
              [percent px] (map #(js/parseFloat (or (second (re-find % calc-expression)) "0"))
                                [#"([0-9.]+)%" #"([0-9.]+)px"])]
          (- percent px)) ;; Calculate the result: percentage minus pixels
        (js/parseFloat value))))) ;; Standard percentage parsing

(defn evidence-for-content
  "Main layout for the pdF viewer."
  [{:keys [active-highlight
           all-analysis
           highlighted
           set-selected-construct]}]
  [:<>
   [:f> analysis.tree/AnalysisTree
    {:hide-category false
     :hide-category-selection false
     :on-click-construct set-selected-construct
     :on-select set-selected-construct
     :preselected-version-ids #{}
     :selected-version-ids #{}
     :display-construct-ids (analysis.tree/get-analysis-tree-displayed-construct-ids
                             (vec all-analysis)
                             #{(:construct-id 1)})
     :category (or "Policy"
                   "Operations (process)")
     :on-click-state (fn [construct-id structure]
                       (analysis.modals/show-editor-modal
                        :edit
                        construct-id
                        (-> structure :applied-analyses first)))
     :loading false
     :analyses (vec all-analysis)
     :search-result-construct-ids (->> highlighted
                                       (filter
                                        (fn [{:keys [evidence-id]}]
                                          (= (str evidence-id)
                                             (str (:evidence-id active-highlight)))))
                                       first
                                       :construct-ids
                                       set)
     :active-query true ;(not (str/blank? query))
     :row-component (fn [props asa]
                      [analysis.tree/ListItemAnalysisRow
                       props
                       asa])}]])


(defn document-evidence-list
  "Left-side menu for evidence and displaying the active evidence"
  [{:keys [active-highlight
           all-analysis
           hide-add-buttons
           highlighted
           is-obsolete
           on-add-analysis-to-document-evidence
           on-scroll-to-evidence
           set-active-highlight
           set-selected-construct
           title-suffix]}]
  (let [[ref set-ref] (react/useState nil)]
    (react/useEffect
     (fn []
       (js/setTimeout
        (fn [] (some-> ref
                       (.scrollIntoView
                        #js{:behavior "smooth"
                            :block "center"})))))
     #js [ref])
    [:<>
     (let [evidence-for-analysis (->> highlighted
                                      (group-by
                                       (comp :start
                                             :page-range
                                             :content-data))
                                      (sort-by key))]
       (if-not (seq highlighted)
         [:div
          [:h4.title.is-6.my-5 "No Evidence Recorded yet"]
          [:p "Either select text or use Recommended Analysis to capture evidence."]]
         [:div.document-viewer--highlight-info
          [:div.title.is-5.my-0
           (or title-suffix "Document Evidence")]
          (->> evidence-for-analysis
               (mapv
                (fn [[construct-id evidence]]
                  [:div.document-viewer--highlight-info-construct
                   [:span.title.is-6.my-0
                    "Page "
                    (-> evidence
                        first
                        :content-data
                        :page-range
                        :start
                        inc)]
                   (->> evidence
                        flatten
                        (sort-by (comp extract-top-percentage
                                       first
                                       flatten
                                       :html-ranges
                                       :content-data))
                        (group-by :evidence-id)
                        (map
                         (fn [[_ [{:keys [evidence-id content] :as data}]]]
                           [:div
                            (merge
                             {:class [:document-viewer--highlight-info-construct
                                      (when (= evidence-id (:evidence-id active-highlight))
                                        :document-viewer--highlight-info-selected-construct)]}
                             (when (= evidence-id (:evidence-id active-highlight))
                               {:ref set-ref}))
                            [:div.level-left.mb-2
                             (when-not hide-add-buttons
                               [:button.button.is-primary.m-0
                                {:on-click (partial on-add-analysis-to-document-evidence data)
                                 :disabled is-obsolete}
                                [:i.icon.fas.fa-circle-plus]])
                             [:a {:on-click (fn [_]
                                              (on-scroll-to-evidence evidence-id data)
                                              (if (= evidence-id (:evidence-id active-highlight))
                                                (set-active-highlight nil)
                                                (set-active-highlight
                                                 {:evidence-id evidence-id
                                                  :type :evidence})))}
                              [abridged-content-blockquote content]]]
                            (when (= evidence-id (:evidence-id active-highlight))
                              [evidence-for-content
                               {:active-highlight active-highlight
                                :all-analysis all-analysis
                                :highlighted highlighted
                                :set-selected-construct set-selected-construct}])]))
                        (into [:<>]))]))
               (into [:<>]))]))]))

(defn document-selection
  "Displays the selection menu in the right panel"
  [{:keys [search-query
           on-search-query
           selected-text]}
   {:keys [information
           provider]}
   disabled]
  [:div.document-viewer--selection
   [:h6.is-6.my-0 "Selection"]
   [abridged-content-blockquote (if (str/blank? selected-text)
                                  search-query
                                  selected-text)]
   [:div.level-left.is-flex
    [:button.button.my-0.is-primary
     {:disabled disabled
      :class (when (and information provider)
               "is-info")
      :on-click #(on-search-query {:text selected-text
                                   :type :selection})}
     "Use as Evidence"]]])
